import React, { useState } from "react";
import { TiTick } from "react-icons/ti";
import "react-datepicker/dist/react-datepicker.css";
import "@/styles/picker.css";
import DatePicker from "react-datepicker";
import calendar from "@/assets/calendar-2.svg";
import { IoMdCheckmark } from "react-icons/io";
import { dimensionUnits } from "@/utils/constants";
import { AiOutlineDown } from "react-icons/ai";
import { numberWithCommas } from "@/utils/CommonFunctions";
import Select from "react-select";
import Dropdown from "@/components/Global/Dropdown";
import indicator from "@/assets/indicatorDown.svg";

type InputProps = {
  value?: string | number | undefined;
  label?: string;
  placeholder: string;
  type: string;
  name: string;
  error?: string | undefined;
  touched?: boolean | number | undefined;
  isDisabled?: boolean;
  required?: boolean;
  onChange: (e: any) => void;
  showLabel?: boolean;
  icon?: any;
  setFieldTouched?: any;
  readOnly?: boolean;
  handleIconClick?: any;
  rightIcon?: any;
  autoComplete?: string;
  autoFocus?: boolean;
  onFocusText?: (e: any) => void;
  onBlurText?: (e: any) => void;
};

export default function Input({
  value,
  label,
  placeholder,
  type,
  name,
  error,
  touched,
  isDisabled,
  required,
  onChange,
  icon,
  setFieldTouched,
  readOnly,
  handleIconClick,
  rightIcon,
  autoComplete,
  autoFocus,
  onFocusText,
  onBlurText,
}: InputProps) {
  const showError = touched && error;

  return (
    <label className="block">
      {(label || required) && (
        <span className=" text-sm text-black-secondary mb-2 font-medium flex capitalize">
          {label}
          {required && <span className="text-xs text-error-500 ml-1">*</span>}
        </span>
      )}
      <div className=" relative">
        <input
          className={`${showError ? "border-error-300 shadow-sm" : "border-gray-300"} [&:-webkit-autofill]:bg-white [&:-webkit-autofill]:shadow-[0_0_0_30px_white_inset] min-h-[42px] 
          placeholder:text-base placeholder:text-gray-500 text-black-primary block bg-white w-full border rounded-md py-2 px-3
         shadow-sm focus:ring-brand-300 focus:border-brand-300 focus:ring-0 focus:outline-none focus:ring-offset-2 focus:ring-offset-brand-50 
         text-base disabled:bg-gray-50 disabled:border-gray-300`}
          name={name}
          type={type}
          placeholder={placeholder}
          onBlur={(e) => {
            setFieldTouched ? setFieldTouched(name, false) : "";
            onBlurText ? onBlurText(e) : "";
          }}
          onFocus={(e) => {
            setFieldTouched ? setFieldTouched(name, true) : "";
            onFocusText ? onFocusText(e) : "";
          }}
          value={value}
          onChange={onChange}
          disabled={isDisabled || readOnly}
          readOnly={readOnly}
          style={{
            paddingLeft: icon ? "32px" : "12px",
            paddingRight: rightIcon ? "32px" : "12px",
          }}
          autoComplete={autoComplete || "on"}
          autoFocus={autoFocus}
        />
        {icon && (
          <div
            className="absolute left-1 px-2 text-black-secondary"
            style={{ top: "13px" }}
          >
            {icon}
          </div>
        )}
        {rightIcon && (
          <div
            className=" absolute right-3 top-3 cursor-pointer"
            onClick={handleIconClick}
          >
            {rightIcon}
          </div>
        )}
      </div>
      {showError && <p className="text-base text-error-500">{error}</p>}
    </label>
  );
}

type CheckBoxInputProps = {
  checked: boolean;
  label: string;
  name: string;
  error?: string | undefined;
  touched?: boolean | number;
  isDisabled?: boolean;
  required?: boolean;
  onChange: (e: any) => void;
  showLabel?: boolean;
  setFieldTouched?: any;
  readOnly?: boolean;
  onClick?: (e: any) => void;
  color?: string;
};

export const CheckboxInput = ({
  checked,
  label,
  name,
  isDisabled,
  onChange,
  setFieldTouched,
  readOnly,
  onClick,
  color,
}: CheckBoxInputProps) => {
  return (
    <label className="inline-flex cursor-pointer">
      <div className="inline-flex  items-center relative justify-center">
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={onChange}
          disabled={isDisabled}
          onClick={onClick}
          readOnly={readOnly}
          onBlur={(e) => {
            setFieldTouched ? setFieldTouched(name, false) : "";
          }}
          onFocus={(e) => {
            setFieldTouched ? setFieldTouched(name, true) : "";
          }}
          className="form-checkbox size-4 border-[#A5A5A6] placeholder:text-gray-500  appearance-none checked:bg-brand-50  checked:border-brand-600 border rounded-[4px] checked:relative disabled:bg-gray-50 disabled:border-gray-300 disabled:cursor-not-allowed"
        />
        {checked && (
          <IoMdCheckmark
            className="text-customGreen-primary absolute"
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>

      {label && (
        <span
          className={`ml-3 ${color ? color : "text-gray-600"} font-medium text-sm`}
        >
          {label}
        </span>
      )}
    </label>
  );
};

type DateInputProps = {
  value: Date;
  label?: string;
  name: string;
  error?: string | undefined;
  touched?: boolean | number;
  placeholder?: string;
  required?: boolean;
  onChange: (e: any) => void;
  showLabel?: boolean;
  setFieldTouched?: any;
  readOnly?: boolean;
  disabled?: boolean;
  minDate?: Date;
  range?: boolean;
  startDate?: Date;
  endDate?: Date;
};

export const DateInput = ({
  value,
  label,
  placeholder,
  name,
  onChange,
  disabled,
  required,
  range,
  startDate,
  endDate,
  error,
  touched,
  // minDate,
}: DateInputProps) => {
  const showError = touched && error;
  return (
    <label className="block">
      {label && (
        <span className="flex text-sm text-black-secondary text-left mb-2 font-medium">
          {label}
          {required && <p className="text-sm text-red-500 ml-1">*</p>}
        </span>
      )}
      <div className="relative">
        <DatePicker
          className="border text-black-primary placeholder:text-gray-500 placeholder:text-base border-gray-300 text-base rounded-lg block w-full pl-2.5 p-2 focus:outline-none focus:border-sky-500
    focus:ring-sky-500 focus:ring-1  disabled:bg-gray-50 disabled:border-gray-300 min-h-[42px]"
          placeholderText={placeholder}
          selected={value}
          dateFormat={"MM/dd/yyyy"}
          popperPlacement="bottom-start"
          onChange={onChange}
          name={name}
          disabled={disabled}
          autoComplete="off"
          startDate={startDate}
          endDate={endDate}
          selectsRange={range || false}
          maxDate={endDate}
          minDate={startDate}
          // minDate={minDate}
        />
        <span className="absolute inset-y-0 right-3 flex items-center">
          <img src={calendar} className="text-2xl" />
        </span>
      </div>
      {showError && <p className="text-base text-error-500">{error}</p>}
    </label>
  );
};

type UnitInputProps = {
  value: string | number | undefined;
  label: string;
  placeholder?: string;
  onChange?: (e: any) => void;
  readOnly?: boolean;
  unitValue: string;
  name: string;
  unitName: string;
  onUnitChange?: (e: any) => void;
  onCreateNew?: (inputValue?: string) => void;
  isCreateNew?: boolean;
  isLoading?: boolean;
  options?: any;
  onFocusText?: (e: any) => void;
  onBlurText?: (e: any) => void;
  setFieldTouched?: any;
  touched?: boolean;
  customOptions?: any;
};

const currencyOptions = [
  { value: "%", label: "%" },
  { value: "curr", label: "$" },
];

export const InputWithUnit = ({
  value,
  label,
  placeholder,
  onChange,
  readOnly,
  unitValue,
  name,
  unitName,
  onUnitChange,
  onCreateNew,
  isCreateNew,
  isLoading,
  options,
  onFocusText,
  onBlurText,
  setFieldTouched,
  touched,
  customOptions,
}: UnitInputProps) => {
  const showError = false;
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  return (
    <div>
      <span className="text-sm text-black-secondary font-medium mb-2 flex text-left">
        {label}
      </span>
      <div
        className={` 
          ${
            label === "Qty"
              ? `min-h-[42px] flex items-center border text-sm border-gray-300 rounded-md  justify-between`
              : `min-h-[42px] flex items-center placeholder:text-base placeholder:text-gray-500 justify-between
        text-black-primary border rounded-md shadow-sm ${readOnly ? "bg-gray-50" : "bg-white"}
        `
          }
        
        ${isFocused ? "border-brand-300 ring-brand-300 ring-0 ring-offset-2 outline-none ring-offset-brand-50" : showError ? "border-error-300 shadow-sm" : "border-gray-300"}
       sm:text-sm`}
      >
        <input
          type="text"
          name={name}
          value={numberWithCommas(value, touched)}
          onChange={onChange}
          placeholder={placeholder || "00.00"}
          readOnly={readOnly}
          disabled={readOnly}
          className="p-2 w-3/6 border-none text-base focus:ring-0 focus:outline-none  placeholder:text-gray-500 disabled:bg-gray-50 disabled:border-gray-300"
          onFocus={(e) => {
            handleFocus();
            setFieldTouched ? setFieldTouched(name, true) : "";
            onFocusText ? onFocusText(e) : "";
          }}
          onBlur={(e) => {
            handleBlur();
            setFieldTouched ? setFieldTouched(name, false) : "";
            onBlurText ? onBlurText(e) : "";
          }}
        />
        <div className={`${label === "Qty" ? "w-2/6 max-w-32" : "min-w-3/6"}`}>
          <Dropdown
            inputInside={true}
            placeholder=""
            options={
              customOptions
                ? customOptions
                : label === "Qty"
                  ? options
                  : currencyOptions
            }
            isDisabled={readOnly}
            value={
              customOptions
                ? customOptions?.find((u) => u.value === unitValue)
                : label === "Qty"
                  ? options?.find((u) => u.value === unitValue)
                  : currencyOptions.find((u) => u.value === unitValue)
            }
            handleChange={
              onUnitChange
                ? (e) =>
                    onUnitChange({ target: { name: unitName, value: e.value } })
                : (e) =>
                    onChange({ target: { name: unitName, value: e.value } })
            }
            isCreateNew={isCreateNew}
            onCreateNew={onCreateNew}
            createNewLabel="Add new"
            isLoading={isLoading}
          />
        </div>
        {/* <select
          value={unitValue}
          onChange={onUnitChange ? onUnitChange : onChange}
          name={unitName}
          disabled={readOnly}
          className={` border-none focus:ring-0 focus:outline-none disabled:bg-gray-50  disabled:border-gray-300 ${label === "Qty" ? "col-span-6" : ""}`}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {label === "Qty" ? (
            <>
              <option value="each">Each</option>
              <option value="yard">Yard</option>
              <option value="inches">Inches</option>
              <option value="meters">Meters</option>
              <option value="centimeters">Centimeters</option>
              <option value="feet">Feet</option>
            </>
          ) : (
            <>
              <option value="%">%</option>
              <option value="curr">$</option>
            </>
          )}
        </select> */}

        {/* <AiOutlineDown className="absolute  top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-500" /> */}
      </div>
    </div>
  );
};

export function DimensionsInput({ values, handleChange }) {
  const showError = false;
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  return (
    <div>
      <span className="text-sm text-black-secondary mb-2 flex font-medium">
        Dimensions
      </span>
      <div
        className={`overflow-hidden grid grid-cols-4 placeholder:text-base placeholder:text-gray-500
        text-black-primary border rounded-md shadow-sm min-h-[42px] 
        ${isFocused ? "border-brand-300 ring-brand-300 ring-0 ring-offset-2 outline-none ring-offset-brand-50" : showError ? "border-error-300 shadow-sm" : "border-gray-300"}
       sm:text-sm`}
      >
        <div className="flex items-center col-span-3 text-base">
          <input
            type="text"
            name="dimension.width"
            value={values.dimension.width}
            onChange={handleChange}
            placeholder="W"
            className="p-1 px-3 w-full border-r border-customGrey-primary focus:ring-0 focus:outline-none"
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <input
            type="text"
            name="dimension.length"
            value={values.dimension.length}
            onChange={handleChange}
            placeholder="D"
            className="p-1 px-3 w-full border-r border-customGrey-primary focus:ring-0 focus:outline-none"
            onFocus={handleFocus}
            onBlur={handleBlur}
          />

          <input
            type="text"
            name="dimension.height"
            value={values.dimension.height}
            onChange={handleChange}
            placeholder="H"
            className="p-1 px-3 w-full border-r border-customGrey-primary focus:ring-0 focus:outline-none"
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <div className="flex items-center justify-between relative">
          <select
            value={values.dimension.unit}
            name="dimension.unit"
            onChange={handleChange}
            className="py-2 px-3 pr-8 w-full border-none focus:ring-0 cursor-pointer focus:outline-none appearance-none mx-auto"
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            {dimensionUnits.map((item) => (
              <option value={item.label}>{item.label}</option>
            ))}
          </select>
          <img
            src={indicator}
            className="absolute right-3 w-3 h-3 cursor-pointer text-gray-500"
          />
        </div>
      </div>
    </div>
  );
}
